<template>
  <div class="w-full bg-white">
    <div class="kb-advanced"
         :class="isAdvancedOpen ? 'open' : 'closed'"
         data-testid="kb-advanced"
    >
      <div class="flex gap-4 items-end">
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <base-label id="kb-query" label="Query" :label-classes="'font-medium'"/>
          <base-input id="kb-query" v-model.trim="query" name="kb-query" variant="editor" class="border-gray-border h-10"
                      placeholder="e.g. white veja sneakers" @keyup.enter="search"/>
        </div>
        <busy-button id="kbSearchButton" variant="primary" class="w-40" :is-loading="isSearching" :is-disabled="!canSearch || isSearching" @click="search">
          Search
        </busy-button>
      </div>

      <button v-if="showUnreleased"
              type="button"
              data-testid="kb-advanced-toggle"
              class="kb-advanced-toggle flex items-center mt-2 p-0 border-0 bg-transparent cursor-pointer text-sm text-gray-dark hover:text-primary-focus"
              :aria-expanded="isAdvancedOpen.toString()"
              aria-controls="kb-advanced-settings"
              @click="isAdvancedOpen = !isAdvancedOpen"
      >
        <span>Search options 👑</span>
        <span class="kb-advanced-chevron">
          <feather-icon icon="ChevronDownIcon" class="h-4 w-4"/>
        </span>
      </button>

      <div id="kb-advanced-settings"
           class="kb-advanced-panel"
           :aria-hidden="!isAdvancedOpen"
           :inert="!isAdvancedOpen"
      >
        <div class="kb-advanced-panel-clip">
          <div class="kb-advanced-panel-inner">
            <div class="flex gap-4 items-end">
              <div class="flex flex-col gap-1 flex-1 min-w-0">
                <base-label id="kb-mode" label="Mode" :label-classes="'font-medium'"/>
                <base-select id="kb-mode" :value="mode" :options="modeOptions" class="h-10" @input="mode = $event"/>
              </div>
              <div class="flex flex-col gap-1" style="width: 7rem">
                <base-label id="kb-topn" label="Top N" :label-classes="'font-medium'"/>
                <base-input id="kb-topn" v-model.number="topN" name="kb-topn" variant="editor" type="number" class="border-gray-border h-10"/>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-1 text-sm text-gray-dark">
              <feather-icon icon="InfoIcon" class="h-4 w-4 flex-shrink-0"/>
              <span>Higher Top N returns more results but may include less relevant content.</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <div v-if="isSearching" class="text-gray">Searching…</div>

      <template v-else-if="results !== null">
        <div v-if="pipeline" class="border-solid border border-gray-border rounded p-4 bg-soft">
          <h3 class="font-semibold text-sm mb-3">RAG pipeline</h3>

          <div class="text-sm mb-2">
            <span class="text-gray-dark">Reformulated query: </span>
            <span class="font-mono">{{ pipeline.reformulated_query }}</span>
          </div>

          <div v-if="pipeline.fell_back" class="text-sm font-medium mb-3">
            ⚠ Filters matched no documents — fell back to an unfiltered search.
          </div>

          <div class="mb-3">
            <div class="text-gray-dark text-sm mb-1">Available filters (from facets)</div>
            <p v-if="!pipeline.filterable_fields.length" class="text-gray text-sm">none for this knowledge base</p>
            <ul v-else class="text-sm pl-0">
              <li v-for="f in pipeline.filterable_fields" :key="f.collection + '.' + f.field" class="font-mono">
                {{ formatFacetField(f) }}
              </li>
            </ul>
          </div>

          <div class="mb-3">
            <div class="text-gray-dark text-sm mb-1">Proposed by the LLM</div>
            <pre class="bg-white border rounded p-2 text-xs overflow-x-auto">{{ formatPayload(pipeline.proposed_filters) }}</pre>
          </div>

          <div>
            <div class="text-gray-dark text-sm mb-1">Applied to the search (validated)</div>
            <pre class="bg-white border rounded p-2 text-xs overflow-x-auto">{{ formatPayload(pipeline.applied_filters) }}</pre>
          </div>
        </div>

        <div class="text-gray-dark text-sm" :class="{ 'mt-4': pipeline }" data-testid="kb-results-summary">
          {{ results.length }} match{{ results.length === 1 ? '' : 'es' }}
        </div>

        <p v-if="!results.length" class="text-gray mt-3">No documents matched this query.</p>

        <div v-if="results.length"
             class="mt-3 border-solid border border-gray-border rounded-lg overflow-hidden">
          <div v-for="(result, i) in results"
               :key="getResultKey(result, i)"
               class="kb-result"
               :class="{ 'is-expanded': isResultExpanded(result, i) }"
          >
            <div class="kb-result-row min-w-0 py-4 pr-4 pl-2 text-left cursor-pointer"
                 :data-testid="`result-toggle-${getResultKey(result, i)}`"
                 role="button"
                 tabindex="0"
                 :aria-expanded="isResultExpanded(result, i).toString()"
                 :aria-controls="`result-details-${getResultKey(result, i)}`"
                 @click="toggleResult(result, i)"
                 @keydown.enter.prevent="toggleResult(result, i)"
                 @keydown.space.prevent="toggleResult(result, i)"
            >
              <span class="kb-result-index text-gray-dark text-sm">{{ i + 1 }}</span>

              <span class="min-w-0">
                <span class="block font-medium text-sm text-black break-words">
                  {{ result.title || result.id || `#${i + 1}` }}
                </span>
                <span v-if="getResultSource(result)" class="block text-xs text-gray-dark break-all">
                  {{ getResultSource(result) }}
                </span>
              </span>

              <span class="kb-result-source text-xs text-gray">
                <span v-if="getResultSourceType(result)"
                      class="block"
                      :data-testid="`result-source-type-${getResultKey(result, i)}`"
                >
                  {{ getResultSourceType(result) }}
                </span>
                <span v-if="getResultContentType(result)" class="block">({{ getResultContentType(result) }})</span>
              </span>

              <span class="kb-result-score-group">
                <vx-tooltip position="left"
                            :title="scoreTooltipTitle"
                            :text="scoreTooltipText"
                            class="kb-result-score"
                >
                  <span class="text-primary-focus text-xs">{{ formatScore(result.score) }}</span>
                </vx-tooltip>
                <span class="kb-result-chevron">
                  <feather-icon icon="ChevronDownIcon" class="h-4 w-4 text-gray-dark"/>
                </span>
              </span>
            </div>

            <div :id="`result-details-${getResultKey(result, i)}`"
                 class="kb-result-panel"
                 :aria-hidden="!isResultExpanded(result, i)"
                 :inert="!isResultExpanded(result, i)"
            >
              <div class="kb-result-panel-clip">
                <div class="kb-result-panel-inner px-4 pb-4 pt-2 bg-white">
                  <div v-if="result.content" class="mt-4">
                    <h4 class="font-medium text-sm mb-2">Content</h4>
                    <pre class="result-content-scroll text-sm text-gray-dark whitespace-pre-wrap break-words font-sans overflow-y-auto">{{ result.content }}</pre>
                  </div>

                  <div v-if="getPayloadEntries(result).length" class="mt-4">
                    <h4 class="font-medium text-sm mb-2">Payload</h4>
                    <div class="result-payload-scroll overflow-y-auto">
                      <div v-for="entry in getPayloadEntries(result)"
                           :key="entry.key"
                           data-testid="result-payload-entry"
                           class="grid grid-cols-12 gap-3 py-2 text-xs">
                        <span class="col-span-4 font-mono text-gray-dark break-words">{{ entry.key }}</span>
                        <pre class="col-span-8 font-mono whitespace-pre-wrap break-words overflow-x-auto">{{ formatPayloadValue(entry.value) }}</pre>
                      </div>
                    </div>
                  </div>

                  <p v-if="!result.content && !getPayloadEntries(result).length" class="text-gray text-sm mt-4">
                    No preview data.
                  </p>

                  <a v-if="getResultLink(result)"
                     :href="getResultLink(result).href"
                     :data-testid="`result-link-${getResultKey(result, i)}`"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="inline-flex items-center gap-1 mt-3 text-primary-focus text-sm font-medium hover:underline"
                  >
                    {{ getResultLink(result).label }}
                    <open-in-new-icon class="h-3 w-3"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import indexesService from '@/services/amio/indexes.service'
import BusyButton from '@/components/common/BusyButton'
import toaster from '@/utils/toaster'
import OpenInNewIcon from '@/assets/images/icons/OpenInNewIcon.vue'

const SCORE_TOOLTIP_TITLE = 'Similarity score'
const SCORE_TOOLTIP_TEXT = 'Similarity between the query and this document. Higher scores are closer matches. With cosine similarity, 1.00 is an exact match.'

export default {
  name: 'KnowledgeBaseExplorer',
  components: { BusyButton, OpenInNewIcon },

  props: {
    indexId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      query: '',
      topN: 10,
      mode: 'raw',
      isAdvancedOpen: false,
      modeOptions: [
        { value: 'raw', text: 'Raw vector search' },
        { value: 'rag', text: 'Full RAG pipeline (LLM + filters)' }
      ],
      isSearching: false,
      results: null, // null = not searched yet; [] = searched, no matches
      provider: null,
      lastQuery: null,
      lastMode: null,
      lastTopN: null,
      pipeline: null, // rag-mode only: reformulated query, facets, proposed + applied filters, fell_back
      expandedResultKey: null,
      scoreTooltipTitle: SCORE_TOOLTIP_TITLE,
      scoreTooltipText: SCORE_TOOLTIP_TEXT
    }
  },

  computed: {
    canSearch() {
      return Boolean(this.indexId) && Boolean(this.query && this.query.trim())
    },
    lastModeLabel() {
      const option = this.modeOptions.find(({ value }) => value === this.lastMode)
      return option ? option.text : this.lastMode
    }
  },

  methods: {
    formatScore(score) {
      return typeof score === 'number' ? score.toFixed(2) : '—'
    },

    formatPayload(payload) {
      return JSON.stringify(payload || {}, null, 2)
    },

    formatPayloadValue(value) {
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
    },

    getPayloadEntries(result) {
      return Object.entries(result.payload || {}).map(([key, value]) => ({ key, value }))
    },

    getResultKey(result, index) {
      return result.id || index
    },

    isResultExpanded(result, index) {
      return this.expandedResultKey === this.getResultKey(result, index)
    },

    toggleResult(result, index) {
      const key = this.getResultKey(result, index)
      this.expandedResultKey = this.expandedResultKey === key ? null : key
    },

    getResultSourceType(result) {
      const payload = result.payload || {}
      if (payload.source_id) return 'XML Feed'
      if (payload.source && result.content) return 'Web Page'
      return null
    },

    getResultContentType(result) {
      return result.payload?.product_id ? 'Product' : null
    },

    getResultSource(result) {
      return result.payload?.source || null
    },

    getResultLink(result) {
      const payload = result.payload || {}
      if (payload.product_id && (Array.isArray(payload.link) && payload.link[0] || typeof payload.link === 'string')) {
        return { label: 'Open product', href: Array.isArray(payload.link) ? payload.link[0] : payload.link }
      }
      if (payload.source && result.content) return { label: 'Open page', href: payload.source }
      return null
    },

    formatFacetField(field) {
      const head = `${field.field} (${field.type}, ${field.collection})`
      return field.values && field.values.length ? `${head}: ${field.values.join(', ')}` : head
    },

    async search() {
      if (!this.canSearch) return
      this.isSearching = true
      try {
        // an empty number input yields NaN via v-model.number — never send that as top_n
        const topN = Number.isInteger(this.topN) && this.topN > 0 ? this.topN : 10
        const mode = this.mode
        const response = await indexesService.searchKnowledgeBase(this.indexId, { query: this.query, topN, mode })
        this.results = response.results || []
        this.expandedResultKey = null
        this.provider = response.provider || null
        this.lastQuery = response.query || this.query
        this.lastMode = mode
        this.lastTopN = topN
        this.pipeline = mode === 'rag' ? {
          reformulated_query: response.reformulated_query,
          filterable_fields: response.filterable_fields || [],
          proposed_filters: response.proposed_filters || [],
          applied_filters: response.applied_filters || {},
          fell_back: Boolean(response.fell_back)
        } : null
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.isSearching = false
      }
    }
  }
}
</script>

<style scoped>
.result-content-scroll {
  max-height: 16rem;
}

.result-payload-scroll {
  max-height: 20rem;
}

.kb-result {
  border-style: solid;
  border-width: 0 0 1px 4px;
  border-color: #DCE1EA;
  border-left-color: #ffffff;
  transition: border-left-color 0.3s ease-in-out, background-color 0.3s ease-in-out;
}

.kb-result:last-child {
  border-bottom-width: 0;
}

.kb-result:hover {
  background-color: #F2F4F8;
  border-left-color: #F2F4F8;
}

.kb-result.is-expanded,
.kb-result.is-expanded:hover {
  border-left-color: rgb(110, 64, 255);
}

.kb-result-row {
  display: grid;
  grid-template-columns: 1.25rem minmax(0, 1fr) 5.5rem auto;
  align-items: baseline;
  column-gap: 0.75rem;
}

.kb-result-index {
  text-align: right;
}

.kb-result-source {
  text-align: right;
}

.kb-result-score-group {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.kb-result-row ::v-deep .kb-result-score {
  display: inline;
}

.kb-result-chevron {
  display: inline-flex;
  transition: transform 0.3s ease-in-out;
}

.kb-result.is-expanded .kb-result-chevron {
  transform: rotate(180deg);
}

.kb-result-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease-in-out;
}

.kb-result.is-expanded .kb-result-panel {
  grid-template-rows: 1fr;
}

.kb-result-panel-clip {
  min-height: 0;
  overflow: hidden;
}

.kb-result.is-expanded .kb-result-panel-clip {
  overflow: visible;
}

.kb-result-panel-inner {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.kb-result.is-expanded .kb-result-panel-inner {
  opacity: 1;
}

.kb-advanced-chevron {
  display: inline-flex;
  margin-left: 0.125rem;
  transition: transform 0.3s ease-in-out;
}

.kb-advanced.open .kb-advanced-chevron {
  transform: rotate(180deg);
}

.kb-advanced-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease-in-out;
}

.kb-advanced.open .kb-advanced-panel {
  grid-template-rows: 1fr;
}

.kb-advanced-panel-clip {
  min-height: 0;
  overflow: hidden;
}

.kb-advanced.open .kb-advanced-panel-clip {
  overflow: visible;
}

.kb-advanced-panel-inner {
  margin-top: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.kb-advanced.open .kb-advanced-panel-inner {
  opacity: 1;
}
</style>
